import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useHomeStats } from '../hooks/useHomeStats';

const STATUS_BG: Record<string, string> = {
  '未执行': 'bg-amber-50 text-amber-600',
  '执行中': 'bg-cyan-50 text-cyan-600',
  '已完成': 'bg-teal-50 text-teal-600',
  '已取消': 'bg-rose-50 text-rose-500',
  '已归档': 'bg-gray-100 text-gray-500',
};

interface CustomLabelProps {
  cx?: number;
  cy?: number;
  midAngle?: number;
  innerRadius?: number;
  outerRadius?: number;
  percent?: number;
}

function CustomLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent }: CustomLabelProps) {
  if (percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

interface TooltipProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number; payload: { color: string } }>;
}

function CustomTooltip({ active, payload }: TooltipProps) {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <div className="bg-white border border-gray-100 rounded-xl px-3 py-2 text-xs">
      <div className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full" style={{ background: p.payload.color }} />
        <span className="text-gray-700 font-medium">{p.name}</span>
        <span className="text-gray-500 ml-1">{p.value} 单</span>
      </div>
    </div>
  );
}

export default function OrderDistribution() {
  const { data, loading } = useHomeStats();

  const total = data?.statusBreakdown.reduce((s, d) => s + d.value, 0) ?? 0;

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-4 md:p-6">
      <h3 className="text-sm md:text-base font-semibold text-gray-900 mb-4 md:mb-5">订单状态分布</h3>

      {loading ? (
        <div className="h-52 md:h-64 flex items-center justify-center">
          <div className="w-8 h-8 flex items-center justify-center">
            <i className="ri-loader-4-line animate-spin text-2xl text-gray-200" />
          </div>
        </div>
      ) : !data || total === 0 ? (
        <div className="h-52 md:h-64 flex flex-col items-center justify-center gap-2">
          <div className="w-10 h-10 flex items-center justify-center">
            <i className="ri-pie-chart-line text-3xl text-gray-200" />
          </div>
          <p className="text-xs text-gray-400">暂无订单数据</p>
        </div>
      ) : (
        <>
          <div className="relative h-44 md:h-52">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.statusBreakdown}
                  cx="50%"
                  cy="50%"
                  innerRadius="38%"
                  outerRadius="70%"
                  dataKey="value"
                  nameKey="name"
                  labelLine={false}
                  label={CustomLabel}
                  strokeWidth={2}
                  stroke="#fff"
                >
                  {data.statusBreakdown.map((entry, i) => (
                    <Cell key={`cell-${i}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
                />
              </PieChart>
            </ResponsiveContainer>
            {/* Center label */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <p className="text-2xl md:text-3xl font-bold text-gray-900 leading-none">{total}</p>
              <p className="text-xs text-gray-400 mt-0.5">总订单</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 mt-3">
            {data.statusBreakdown.map((item) => (
              <div key={item.name} className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-xl">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: item.color }} />
                <div className="min-w-0 flex-1">
                  <p className={`text-[11px] font-medium truncate ${(STATUS_BG[item.name] ?? '').split(' ')[1] ?? 'text-gray-600'}`}>
                    {item.name}
                  </p>
                  <div className="flex items-baseline gap-1">
                    <p className="text-sm font-bold text-gray-800">{item.value}</p>
                    <p className="text-[10px] text-gray-400">单</p>
                    <p className="text-[10px] text-gray-400 ml-auto">
                      {total > 0 ? `${((item.value / total) * 100).toFixed(0)}%` : '0%'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
